import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import {
  setWsConnection,
  setConnected,
  setUserConnected,
  setUserDisconnected,
  addMessage,
  setUserTyping,
  endCall,
  setChatData,
  updateChatData,
  setCall,
  setOffer,
  setShowAppointmentEndPopup,
  setConnectionError,
} from "../slices/chat.slice";
import { clearWsConnection } from "../slices/chat.slice";
import { RootState } from "../types";
import { IChatMessage, IUserChatData } from "../../types/types";
import {
  BASE_URL,
  END_APPOINTMENT,
  END_CALL_CODE,
  TYPING_CODE,
} from "../../utils/constants";
import { tWithKeyAttr as t } from "../../i18nHelper";



function attachSignalRHandlers(connection: HubConnection, dispatch: any) {
  connection.on("ReceiveChatData", (data: IUserChatData) => {
    dispatch(setChatData(data));
  });
  connection.on(
    "ReceiveChatDataUsers",
    (data: { userId: number; online: boolean }[]) => {
      dispatch(updateChatData(data));
    }
  );
  connection.on("UserConnected", (data: number) => {
    dispatch(setUserConnected(data));
  });
  connection.on("UserDisconnected", (disconnectedUserId: number) => {
    // console.log("User disconnected:", disconnectedUserId);
    dispatch(setUserDisconnected(disconnectedUserId));
  });
  connection.on("ReceiveError", (data: any) => {
    // console.log(data);
  });
  connection.on("onMessage", (message: IChatMessage) => {
    if (message.content.includes(TYPING_CODE)) {
      dispatch(setUserTyping(message));
    } else if (message.content.includes(END_CALL_CODE)) {
      dispatch(endCall(message));
    } else if (message?.type === END_APPOINTMENT) {
      dispatch(setShowAppointmentEndPopup(message.content));
    } else {
      dispatch(addMessage(message));
    }
  });
  connection.on(
    "ReceiveOffer",
    (offer: { offer: string; from: { userId?: number; name: string; avatar?: string } }) => {
      dispatch(setCall({ isReceivingCall: true, from: offer.from }));
      dispatch(setOffer(offer.offer));
    }
  );
}

// ---------------------------------------------------------------------------
// Guest connection builder — recursive, re-fetches token on every attempt
// so an expired token never blocks reconnection.
// ---------------------------------------------------------------------------

async function buildGuestConnection(
  guid: string,
  appointmentId: number,
  dispatch: any,
  attempts = 0
): Promise<void> {
  if (attempts > 5) {
    dispatch(setConnectionError(t("CONNECTION_LOST")));
    return;
  }

  if (attempts > 0) {
    await new Promise<void>((r) => setTimeout(r, 2000 * attempts));
  }

  let token: string;
  try {
    const response = await fetch(`${BASE_URL}api/chat/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guid, appointmentId }),
    });

    if (response.status === 401) {
      dispatch(setConnectionError("This link is not valid."));
      return;
    }
    if (!response.ok) {
      // Transient server error — retry
      return buildGuestConnection(guid, appointmentId, dispatch, attempts + 1);
    }

    const data = await response.json();
    token = data.token;
  } catch (err) {
    // console.error("Guest token fetch failed:", err);
    return buildGuestConnection(guid, appointmentId, dispatch, attempts + 1);
  }

  // Token is kept only in this closure — never written to storage.
  const connection = new HubConnectionBuilder()
    .withUrl(`${BASE_URL}guestchathub?token=${token}`, {
      withCredentials: false,
    })
    .configureLogging(LogLevel.Information)
    .build();

  connection.onclose(async () => {
    dispatch(setConnected(false));
    if ((connection as any).__intentionalStop) return;
    // Re-fetch a fresh token before reconnecting (old one may have expired).
    await buildGuestConnection(guid, appointmentId, dispatch);
  });

  attachSignalRHandlers(connection, dispatch);

  try {
    await connection.start();
    dispatch(setWsConnection(connection));
    dispatch(setConnected(true));
    dispatch(setConnectionError(undefined));
  } catch (err) {
    // console.error(`Guest connection attempt ${attempts + 1} failed:`, err);
    await buildGuestConnection(guid, appointmentId, dispatch, attempts + 1);
  }
}


export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/" }),
  endpoints: (builder) => ({
    // -----------------------------------------------------------------------
    // Authenticated user flow → /chathub
    // -----------------------------------------------------------------------
    initializeConnection: builder.query<
      void,
      { userId: string; shouldConnect: boolean }
    >({
      // @ts-ignore
      async queryFn({ userId, shouldConnect }, { dispatch, getState }) {
        if (!shouldConnect)
          return { error: { message: "appointment is no longer valid" } };

        const existingConnection = (getState() as RootState).chat.connection;
        if (existingConnection) {
          if (existingConnection.state !== HubConnectionState.Disconnected) {
            return { data: null };
          }
          try {
            await existingConnection.start();
            dispatch(setWsConnection(existingConnection));
            dispatch(setConnected(true));
            return { data: null };
          } catch (error) {
            console.warn(
              "Existing connection failed to start, creating a new one",
              error
            );
          }
        }
        const connection = new HubConnectionBuilder()
          .withUrl(`${BASE_URL}chathub`, {
            // Read token fresh on every request — handles token refresh correctly
            accessTokenFactory: () => {
              const currentState = getState() as RootState;
              return currentState.user.accessToken ?? "";
            },
            withCredentials: false,
          })
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .build();

        connection.onreconnecting((error) => {
          // console.warn(`Connection lost due to error "${error}". Reconnecting...`);
          dispatch(setConnected(false));
        });
        connection.onreconnected((connectionId) => {
          // console.log("Connection reestablished with connectionId:", connectionId);
          dispatch(setConnected(true));
        });
        connection.onclose((error) => {
          // console.warn("Connection closed", error);
          dispatch(setConnected(false));
        });

        attachSignalRHandlers(connection, dispatch);

        const attemptReconnection = async () => {
          for (let attempt = 0; attempt < 5; attempt++) {
            if (connection.state === HubConnectionState.Disconnected) {
              try {
                await connection.start();
                dispatch(setWsConnection(connection));
                dispatch(setConnected(true));
                break;
              } catch (error) {
                // console.error(`Reconnection attempt ${attempt + 1} failed. Retrying...`);
                await new Promise((resolve) => setTimeout(resolve, 3000));
              }
            }
          }
        };

        if (connection.state === HubConnectionState.Disconnected) {
          try {
            await connection.start();
            dispatch(setWsConnection(connection));
            dispatch(setConnected(true));
            return { data: null };
          } catch (error: any) {
            // console.error("Connection error during start:", error);
            await attemptReconnection();
            return { error };
          }
        } else {
          // console.warn("Connection is already started or not in 'Disconnected' state.");
          return { error: "Connection is not in 'Disconnected' state." };
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Guest flow → POST /api/chat/token, then /guestchathub
    // -----------------------------------------------------------------------
    initializeGuestConnection: builder.query<
      void,
      { guid: string; appointmentId: number }
    >({
      // @ts-ignore
      async queryFn({ guid, appointmentId }, { dispatch, getState }) {
        // Stop any existing connection before starting a guest one.
        const existingConnection = (getState() as RootState).chat.connection;
        if (
          existingConnection &&
          existingConnection.state !== HubConnectionState.Disconnected
        ) {
          try {
            (existingConnection as any).__intentionalStop = true;
            await existingConnection.stop();
          } catch (e) {
            console.warn("Error stopping existing connection", e);
          }
        }

        await buildGuestConnection(guid, appointmentId, dispatch);
        return { data: null };
      },
    }),

    // -----------------------------------------------------------------------
    // Shared disconnect
    // -----------------------------------------------------------------------
    disconnectConnection: builder.mutation<void, void>({
      // @ts-ignore
      async queryFn(_arg, { dispatch, getState }) {
        const connection = (getState() as RootState).chat.connection;
        if (connection) {
          try {
            (connection as any).__intentionalStop = true;
            await connection.stop();
          } catch (e) {
            console.warn("Error stopping connection", e);
          } finally {
            dispatch(clearWsConnection());
            dispatch(setConnected(false));
          }
        }
        return { data: null as any };
      },
    }),
  }),
});

export const {
  useLazyInitializeConnectionQuery,
  useLazyInitializeGuestConnectionQuery,
  useDisconnectConnectionMutation,
} = chatApi;
